import { useState } from 'react';
import Drawer from '~/components/Drawer';
import { inputStyles, labelStyles, requiredStyles } from './styles';
import { updateApplicationAgreements } from '~/services/campaign';

interface Props {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    application?: unknown;
    applicationId?: number;
    onUpdate?: (response: any) => void;
}

const IDENTITY_CHOICES = [
    { value: 1, label: 'Client' },
    { value: 2, label: 'Spouse' },
    { value: 3, label: 'Parent of minor client' },
    { value: 4, label: 'Legal Guardian' },
    { value: 5, label: 'POA' },
];

const AgreementsDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    application,
    applicationId,
    onUpdate,
}) => {
    const applicationData = application as {
        ag_certify_identity?: number;
        ag_certify_info?: boolean;
        ag_medpro_required?: boolean;
        ag_signature?: string;
        ag_signature_date?: string;
        ag_terms_of_service?: boolean;
        ag_understand_funds_come_to_hhl?: boolean;
        ag_understand_hhl_may_use_photos?: boolean;
        ag_understand_hhl_publishes_health_info?: boolean;
        ag_understand_hhl_may_use_public_content?: boolean;
    };

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        certifyIdentity: applicationData?.ag_certify_identity || '',
        certifyInfo: applicationData?.ag_certify_info || false,
        medproRequired: applicationData?.ag_medpro_required || false,
        termsOfService: applicationData?.ag_terms_of_service || false,
        understandFundsComeToHhl:
            applicationData?.ag_understand_funds_come_to_hhl || false,
        understandHhlMayUsePhotos:
            applicationData?.ag_understand_hhl_may_use_photos || false,
        understandHhlPublishesHealthInfo:
            applicationData?.ag_understand_hhl_publishes_health_info || false,
        understandHhlMayUsePublicContent:
            applicationData?.ag_understand_hhl_may_use_public_content || false,
        signature: applicationData?.ag_signature || '',
        signatureDate: applicationData?.ag_signature_date
            ? applicationData.ag_signature_date.split('T')[0]
            : new Date().toISOString().split('T')[0],
    });

    const handleChange = (field: string, value: string | number | boolean) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleCheckboxChange = (field: string, checked: boolean) => {
        setFormData((prev) => ({ ...prev, [field]: checked }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationError(null);

        if (!applicationId) {
            console.error('Application ID is required to save agreements data');
            return;
        }

        // Validate all required fields
        if (!formData.certifyIdentity) {
            setValidationError('Please select your identity certification.');
            return;
        }

        if (
            !formData.certifyInfo ||
            !formData.medproRequired ||
            !formData.termsOfService ||
            !formData.understandFundsComeToHhl ||
            !formData.understandHhlMayUsePhotos ||
            !formData.understandHhlPublishesHealthInfo
        ) {
            setValidationError('Please accept all terms and agreements.');
            return;
        }

        if (!formData.signature.trim()) {
            setValidationError('Please provide your signature.');
            return;
        }

        if (!formData.signatureDate) {
            setValidationError('Please provide the signature date.');
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                ag_certify_identity: formData.certifyIdentity
                    ? parseInt(formData.certifyIdentity.toString())
                    : null,
                ag_certify_info: formData.certifyInfo,
                ag_medpro_required: formData.medproRequired,
                ag_terms_of_service: formData.termsOfService,
                ag_understand_funds_come_to_hhl:
                    formData.understandFundsComeToHhl,
                ag_understand_hhl_may_use_photos:
                    formData.understandHhlMayUsePhotos,
                ag_understand_hhl_publishes_health_info:
                    formData.understandHhlPublishesHealthInfo,
                ag_understand_hhl_may_use_public_content:
                    formData.understandHhlMayUsePublicContent,
                ag_signature: formData.signature,
                ag_signature_date: formData.signatureDate,
                ag_terms_completed: true, // Set to true when form is submitted
            };

            console.log('Saving agreements data:', payload);
            console.log('Application ID:', applicationId);

            const response = await updateApplicationAgreements(
                applicationId,
                payload
            );

            if (onUpdate) {
                onUpdate(response);
            }

            setIsOpen(false);
        } catch (error) {
            console.error('Error saving agreements data:', error);
            // You might want to show an error message to the user here
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Drawer
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            title='Terms & Agreements'
        >
            <form onSubmit={handleSubmit}>
                <div className='space-y-4'>
                    {/* Certification */}
                    <div>
                        <label
                            htmlFor='ag-certify-identity'
                            className={labelStyles}
                        >
                            I certify that I am the:
                            <span className={requiredStyles}>*</span>
                        </label>
                        <select
                            id='ag-certify-identity'
                            className={`${inputStyles} w-full`}
                            value={formData.certifyIdentity}
                            onChange={(e) =>
                                handleChange('certifyIdentity', e.target.value)
                            }
                        >
                            <option value=''>Select</option>
                            {IDENTITY_CHOICES.map((choice) => (
                                <option key={choice.value} value={choice.value}>
                                    {choice.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Agreements Section */}
                    <div className='space-y-3'>
                        <div className='rounded-lg border border-[#E5E5E5] p-3'>
                            <label className='flex items-start'>
                                <input
                                    type='checkbox'
                                    checked={formData.certifyInfo}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'certifyInfo',
                                            e.target.checked
                                        )
                                    }
                                    className='mr-2 mt-1'
                                />
                                <span className='text-[14px]/[20px]'>
                                    The information contained in this application
                                    will be used by Help Hope Live to confirm
                                    medical and financial need. I certify that
                                    the information contained herein is true to
                                    the best of my knowledge, information, and
                                    belief.
                                    <span className={requiredStyles}>*</span>
                                </span>
                            </label>
                        </div>

                        <div className='rounded-lg border border-[#E5E5E5] p-3'>
                            <label className='flex items-start'>
                                <input
                                    type='checkbox'
                                    checked={formData.medproRequired}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'medproRequired',
                                            e.target.checked
                                        )
                                    }
                                    className='mr-2 mt-1'
                                />
                                <span className='text-[14px]/[20px]'>
                                    A medical professional must provide
                                    information confirming my medical need before
                                    Help Hope Live can process any fund requests
                                    I submit.
                                    <span className={requiredStyles}>*</span>
                                </span>
                            </label>
                        </div>

                        <div className='rounded-lg border border-[#E5E5E5] p-3'>
                            <label className='flex items-start'>
                                <input
                                    type='checkbox'
                                    checked={formData.termsOfService}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'termsOfService',
                                            e.target.checked
                                        )
                                    }
                                    className='mr-2 mt-1'
                                />
                                <span className='text-[14px]/[20px]'>
                                    I agree to the Help Hope Live Terms of
                                    Service and will make sure my fundraising
                                    volunteers understand and adhere to the
                                    guidelines set forth in these terms.
                                    <span className={requiredStyles}>*</span>
                                </span>
                            </label>
                        </div>

                        <div className='rounded-lg border border-[#E5E5E5] p-3'>
                            <label className='flex items-start'>
                                <input
                                    type='checkbox'
                                    checked={formData.understandFundsComeToHhl}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'understandFundsComeToHhl',
                                            e.target.checked
                                        )
                                    }
                                    className='mr-2 mt-1'
                                />
                                <span className='text-[14px]/[20px]'>
                                    Because I am working with and using materials
                                    provided to me by a 501(c)(3) nonprofit, all
                                    funds raised for Help Hope Live must come to
                                    Help Hope Live. Failure to send all funds to
                                    Help Hope Live will negate the tax
                                    deductibility of donations. This may have
                                    legal and/or tax implications for me and my
                                    campaign contributors. Funds given directly
                                    to me are not considered tax deductible to
                                    the campaign contributor. I will ensure that
                                    my fundraising volunteers understand this.
                                    <span className={requiredStyles}>*</span>
                                </span>
                            </label>
                        </div>

                        <div className='rounded-lg border border-[#E5E5E5] p-3'>
                            <label className='flex items-start'>
                                <input
                                    type='checkbox'
                                    checked={formData.understandHhlMayUsePhotos}
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'understandHhlMayUsePhotos',
                                            e.target.checked
                                        )
                                    }
                                    className='mr-2 mt-1'
                                />
                                <span className='text-[14px]/[20px]'>
                                    Photographs and video on my Help Hope Live
                                    Campaign Page may be used by Help Hope Live
                                    for media outreach, promotional materials,
                                    web-based fundraising, and any other efforts
                                    my Help Hope Live Client Services Coordinator
                                    may deem necessary to maximize the funds
                                    raised in my honor. I understand that the
                                    information disclosed may include facts about
                                    my general medical condition, financial need,
                                    and progress of my fundraising campaign.
                                    <span className={requiredStyles}>*</span>
                                </span>
                            </label>
                        </div>

                        <div className='rounded-lg border border-[#E5E5E5] p-3'>
                            <label className='flex items-start'>
                                <input
                                    type='checkbox'
                                    checked={
                                        formData.understandHhlPublishesHealthInfo
                                    }
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'understandHhlPublishesHealthInfo',
                                            e.target.checked
                                        )
                                    }
                                    className='mr-2 mt-1'
                                />
                                <span className='text-[14px]/[20px]'>
                                    Once my application has been approved, Help
                                    Hope Live will publish information about my
                                    personal health story and fundraising efforts
                                    online via a customizable campaign page at
                                    www.helphopelive.org, unless privacy is
                                    otherwise specified.
                                    <span className={requiredStyles}>*</span>
                                </span>
                            </label>
                        </div>

                        <div className='rounded-lg border border-[#E5E5E5] p-3'>
                            <label className='flex items-start'>
                                <input
                                    type='checkbox'
                                    checked={
                                        formData.understandHhlMayUsePublicContent
                                    }
                                    onChange={(e) =>
                                        handleCheckboxChange(
                                            'understandHhlMayUsePublicContent',
                                            e.target.checked
                                        )
                                    }
                                    className='mr-2 mt-1'
                                />
                                <span className='text-[14px]/[20px]'>
                                    (Optional) To use any public content on my
                                    Help Hope Live Campaign Page, including but
                                    not limited to photos, videos, and updates,
                                    to help spread the nonprofit mission. I agree
                                    that this public content may be used by Help
                                    Hope Live for promotional content including
                                    brochures, print and digital newsletters,
                                    social media posts, and annual reports. This
                                    optional consent will help us reach and
                                    support more families facing a medical
                                    crisis.
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Signature Section */}
                    <div className='border-t pt-4'>
                        <h3 className='mb-3 text-[14px]/[18px] font-bold'>
                            Signature
                        </h3>

                        <div>
                            <label
                                htmlFor='ag-signature'
                                className={labelStyles}
                            >
                                Name<span className={requiredStyles}>*</span>
                            </label>
                            <input
                                type='text'
                                id='ag-signature'
                                className={`${inputStyles} w-full`}
                                value={formData.signature}
                                onChange={(e) =>
                                    handleChange('signature', e.target.value)
                                }
                                placeholder='Type your full name'
                            />
                            <p className='mt-1 text-[12px]/[16px] text-[#949599]'>
                                By typing your name, you are signing this
                                agreement electronically
                            </p>
                        </div>

                        <div>
                            <label
                                htmlFor='ag-signature-date'
                                className={labelStyles}
                            >
                                Date<span className={requiredStyles}>*</span>
                            </label>
                            <input
                                type='date'
                                id='ag-signature-date'
                                className={`${inputStyles} w-full`}
                                value={formData.signatureDate}
                                onChange={(e) =>
                                    handleChange(
                                        'signatureDate',
                                        e.target.value
                                    )
                                }
                            />
                        </div>
                    </div>
                </div>

                {validationError && (
                    <div className='mt-4 flex items-center gap-2 rounded-lg border border-[#F02D3A] bg-[#FDEAEB] p-4 text-[14px] text-[#F02D3A]'>
                        <svg
                            className='h-5 w-5 flex-shrink-0'
                            fill='currentColor'
                            viewBox='0 0 20 20'
                        >
                            <path
                                fillRule='evenodd'
                                d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                                clipRule='evenodd'
                            />
                        </svg>
                        <span className='font-medium'>{validationError}</span>
                    </div>
                )}

                <div className='mb-[30px] flex gap-[16px]'>
                    <button
                        type='button'
                        className='mt-[30px] grow rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                        onClick={() => setIsOpen(false)}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type='submit'
                        className='mt-[30px] grow rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8]'
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <span className='flex items-center justify-center'>
                                <svg
                                    className='mr-3 h-5 w-5 animate-spin'
                                    viewBox='0 0 24 24'
                                >
                                    <circle
                                        className='opacity-25'
                                        cx='12'
                                        cy='12'
                                        r='10'
                                        stroke='currentColor'
                                        strokeWidth='4'
                                    />
                                    <path
                                        className='opacity-75'
                                        fill='currentColor'
                                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                    />
                                </svg>
                                Saving...
                            </span>
                        ) : (
                            'Save'
                        )}
                    </button>
                </div>
            </form>
        </Drawer>
    );
};

export default AgreementsDrawer;
